import {
  analyzePackageImports,
  ensurePackageBundled,
  type BundleInfo,
  type EnsurePackageBundledContext,
} from '@ms-cloudpack/api-server';
import type { GeneratedPackageChanges } from './types/GeneratedPackageChanges.js';
import {
  evaluateImportsForOverrides,
  type EvaluateImportsForOverridesOptions,
  type EvaluateImportsForOverridesResult,
} from './evaluateImportsForOverrides.js';
import { evaluateDynamicImports } from './evaluateDynamicImports.js';
import type { InitAppOptions } from './types/InitAppOptions.js';

/**
 * A helper which given the packagePath, tries to evaluate the path by bundling things, finding imports,
 * and cross checking the imports with explicit exports. If any exports are missing, they are documented
 * in the generated config.
 */
export async function evaluatePath(
  options: InitAppOptions &
    Pick<EvaluateImportsForOverridesOptions, 'packagePath' | 'importerContext'> & {
      shouldForce: boolean;
      /** The priority of the package being evaluated. To be used in the task runner. */
      priority: number;
    },
  ctx: EnsurePackageBundledContext,
): Promise<
  Partial<Pick<EvaluateImportsForOverridesResult, 'pathsToEvaluate' | 'pathsToRefresh'>> & {
    /** Whether a fatal error was encountered. */
    shouldExit?: boolean;
  }
> {
  const { packagePath, initOptions, span, summaryData, importerContext, shouldForce, isMultiApp, priority } = options;
  const { packages, session } = ctx;
  const { config } = session;
  const { features } = config;

  // This line is the reason we have to track the importerContext for packages to evaluate:
  // otherwise if a non-semver package is read here, we'd lose the modifications from the initial
  // time the package was read (and getNonSemverTransform was called) when building the resolve map.
  const definition = await packages.get(packagePath, { refresh: true, importerContext });
  const { name, version } = definition;

  console.debug(`Evaluating ${packagePath} with context ${JSON.stringify(importerContext)}`);

  let info: BundleInfo | undefined;

  if (features?.staticAnalysisInit) {
    // Use fast static analysis instead of bundling to discover imports.
    try {
      info = await analyzePackageImports({ packagePath, packages });
      summaryData.recordResult(packagePath, { name, version, info });
    } catch (error) {
      summaryData.recordException({
        packagePath,
        error,
        source: 'analyzePackageImports',
        context: 'analyzing package imports',
        name,
        version,
      });
      return {};
    }
  } else {
    // Bundle the package and find the imports it depends on.
    const { result } = await ensurePackageBundled(
      {
        name,
        version,
        isIncremental: false,
        shouldGetBundleInfo: true,
        shouldForce,
        shouldRerun: shouldForce,
        useHashAsId: isMultiApp,
        priority,
        disableCache: !initOptions.cache,
      },
      ctx,
    );

    // Record the results for the summary.
    summaryData.recordResult(packagePath, result);
    info = result.info;

    // If there were errors, return early.
    if (result.errors?.length) {
      return {};
    }
  }

  if (!info) {
    return {};
  }

  // Given the package imports, find the missing exports maps and update packageOverrides.
  const {
    exportsChanges,
    includedDependenciesChanges,
    removedExcludedDependenciesChanges,
    errors: evalErrors,
    warnings: evalWarnings,
    pathsToEvaluate,
    pathsToRefresh,
  } = await evaluateImportsForOverrides({ definition, packagePath, info, importerContext }, ctx);

  if (evalErrors.length) {
    summaryData.addErrors(packagePath, evalErrors);
  }
  if (evalWarnings.length) {
    summaryData.addWarnings(packagePath, evalWarnings);
  }
  summaryData.recordGeneratedChanges(exportsChanges);
  summaryData.recordGeneratedChanges(includedDependenciesChanges);
  summaryData.recordGeneratedChanges(removedExcludedDependenciesChanges);

  let dynamicImportsChanges: undefined | GeneratedPackageChanges[] = undefined;
  if (features?.evaluateDynamicImports) {
    const dynamicImportsResult = await evaluateDynamicImports({ definition, packagePath, info }, ctx);

    dynamicImportsChanges = dynamicImportsResult.dynamicImportsChanges;

    summaryData.recordGeneratedChanges(dynamicImportsChanges);

    if (dynamicImportsResult.shouldReevaluate) {
      pathsToEvaluate.set(packagePath, importerContext);
    }
    if (dynamicImportsResult.shouldRefresh) {
      pathsToRefresh.add(packagePath);
    }
  }

  // Uncomment this when #1694 init does not cache packages correctly after editing config is fixed
  // and the heap does not reach the limit as almost every package is enqueued.
  // importPathsToEvaluate?.forEach((path) => pathsToEvaluate.set(path, importerContext));
  // importPathsToRefresh?.forEach((path) => pathsToRefresh.add(path));

  if (initOptions.check && (exportsChanges.length || evalErrors.length || dynamicImportsChanges?.length)) {
    span.addEvent('INIT_CHECK_FAILED', {
      // Don't log the specific errors to telemetry, as they may contain sensitive information.
      reason: exportsChanges || dynamicImportsChanges ? 'Config needs to be updated.' : 'Errors found with imports.',
    });

    return { shouldExit: true };
  }

  return {
    pathsToEvaluate,
    pathsToRefresh,
  };
}
